import * as React from 'react';
import LineMarker from '../LineMarker';
import type { ILine } from '../../../../../../../../models/line.model';
import Colors from '../../../../../../../../helpers/colors.helper';
import type { ICall } from '../../../../../../../../models/call.model';
import type { ILinkEndpoint } from '../../../../../../../../models/link-endpoint.model';
import { StopAreaHelper } from '../../../../../../../../helpers/stop-area.helper';
import EstimatedAndPlannedTimes from '../shared/EstimatedAndPlannedTimes';
import { LineHelper } from '../../../../../../../../helpers/line.helper';
import './style.scss';
import { CircleDisplayType, LineType } from '../LineMarker/line-markers.enum';
import RealTimeAndPlannedPlatforms from '../shared/RealTimeAndPlannedPlatforms';
import { ICallDetails } from '../../../../../../../../models/call-details.model';

function JourneySection({
  origin = undefined,
  destination = undefined,
  originEndpoint = undefined,
  destinationEndpoint = undefined,
  line = undefined,
  lineColor = undefined,
  lineType = LineType.solid,
  originDetails = undefined,
  destinationDetails = undefined,
  className = '',
  children,
}: {
  origin?: ICall;
  destination?: ICall;
  originEndpoint?: ILinkEndpoint;
  destinationEndpoint?: ILinkEndpoint;
  line?: ILine;
  lineColor?: string;
  lineType?: LineType;
  originDetails?: ICallDetails;
  destinationDetails?: ICallDetails;
  className?: string;
  children: React.ReactNode;
}) {
  const plannedDepartureTime = origin?.plannedTime ?? originEndpoint?.plannedTime;
  const plannedArrivalTime = destination?.plannedTime ?? destinationEndpoint?.plannedTime;

  const estimatedDepartureTime = origin?.estimatedTime ?? originEndpoint?.estimatedTime;
  const estimatedArrivalTime = destination?.estimatedTime ?? destinationEndpoint?.estimatedTime;

  const originName = StopAreaHelper.getNameWithoutMunicipality(origin?.stopPoint.name ?? originEndpoint?.name);
  const destinationName = StopAreaHelper.getNameWithoutMunicipality(destination?.stopPoint.name ?? destinationEndpoint?.name);

  let circleDisplayType: CircleDisplayType = CircleDisplayType.none;

  if (line) circleDisplayType = CircleDisplayType.both;
  else if (originEndpoint && destinationEndpoint) circleDisplayType = CircleDisplayType.both;
  else if (originEndpoint) circleDisplayType = CircleDisplayType.firstOnly;
  else if (destinationEndpoint) circleDisplayType = CircleDisplayType.secondOnly;

  return (
    <li className={`journey-section d-flex overflow-hidden print-no-break ${className}`}>
      <LineMarker
        foregroundColor={LineHelper.getBackgroundSafeForegroundColor(line) ?? Colors.vtdGray700}
        backgroundColor={LineHelper.getBackgroundSafeBackgroundColor(line) ?? Colors.vtdWhite}
        lineColor={lineColor ?? Colors.vtdGray700}
        lineType={lineType}
        circleDisplayType={circleDisplayType}
        className="flex-shrink-0"
      />
      <div className="flex-grow-1 ms-2">
        <EstimatedAndPlannedTimes
          estimatedTime={estimatedDepartureTime}
          plannedTime={plannedDepartureTime}
          isCancelled={(originDetails?.isCancelled && originDetails?.isDepartureCancelled) ?? false}
          className="mb-1 journey-section__times"
        />
        {originName && (
          <div className="mb-2">
            <span className="h4 me-1">{originName}</span>
            <RealTimeAndPlannedPlatforms stopPoint={origin?.stopPoint} realtimeStopPoint={origin?.realtimeStopPoint} className="text-color-gray-800" />
          </div>
        )}
        <div className={`${originName ? 'mb-2' : 'my-2'}`}>{children}</div>
        {destinationName && (
          <div className="mb-1">
            <span className="h4 me-1">{destinationName}</span>
            <RealTimeAndPlannedPlatforms stopPoint={destination?.stopPoint} realtimeStopPoint={destination?.realtimeStopPoint} className="text-color-gray-800" />
          </div>
        )}
        <EstimatedAndPlannedTimes
          estimatedTime={estimatedArrivalTime}
          plannedTime={plannedArrivalTime}
          isCancelled={(destinationDetails?.isCancelled && destinationDetails?.isArrivalCancelled) ?? false}
          className="journey-section__times"
        />
      </div>
    </li>
  );
}

export default JourneySection;
